import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../services/api';
import { useEmbeddedMode } from '../hooks/useEmbeddedMode';
import BulkInvoiceManager from '../components/BulkInvoiceManager';
import {
  DocumentPlusIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  ChartBarIcon,
  CloudArrowUpIcon,
  QrCodeIcon,
  TableCellsIcon,
} from '@heroicons/react/24/outline';

const Dashboard = () => {
  const { user } = useAuth();
  const isEmbedded = useEmbeddedMode();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recentInvoices, setRecentInvoices] = useState([]);
  const [showBulkManager, setShowBulkManager] = useState(false);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      
      // Load invoice statistics
      const statsResponse = await api.invoices.getStats();
      setStats(statsResponse.data.stats);

      // Load recent invoices
      const invoicesResponse = await api.invoices.getAll({ 
        page: 1, 
        limit: 5, 
        sortBy: 'created_at', 
        sortOrder: 'DESC' 
      });
      setRecentInvoices(invoicesResponse.data.invoices);

    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Submitted':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      case 'Validated':
        return <CheckCircleIcon className="h-5 w-5 text-blue-500" />;
      case 'Error':
        return <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />;
      default:
        return <ClockIcon className="h-5 w-5 text-yellow-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Submitted':
        return 'bg-green-100 text-green-800';
      case 'Validated':
        return 'bg-blue-100 text-blue-800';
      case 'Error':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-yellow-100 text-yellow-800';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Welcome Section */}
      <div className="bg-white shadow rounded-lg p-6">
        <h1 className="text-2xl font-bold text-gray-900">
          Welcome back, {user?.business_name}!
        </h1>
        <p className="mt-1 text-sm text-gray-600">
          Manage your digital invoices and submit them to FBR through the PRAL DI API.
        </p>
      </div>

      {/* Quick Actions */}
      {!isEmbedded && (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        <Link
          to="/app/scenarios"
          className="bg-white p-6 rounded-lg shadow hover:shadow-md transition-shadow border-l-4 border-blue-500"
        >
          <div className="flex items-center">
            <DocumentPlusIcon className="h-8 w-8 text-blue-500" />
            <div className="ml-4">
              <h3 className="text-lg font-medium text-gray-900">Create New Invoice</h3>
              <p className="text-sm text-gray-600">Start a new invoice submission</p>
            </div>
          </div>
        </Link>

        <Link
          to="/app/invoices"
          className="bg-white p-6 rounded-lg shadow hover:shadow-md transition-shadow border-l-4 border-green-500"
        >
          <div className="flex items-center">
            <DocumentTextIcon className="h-8 w-8 text-green-500" />
            <div className="ml-4">
              <h3 className="text-lg font-medium text-gray-900">View All Invoices</h3>
              <p className="text-sm text-gray-600">Manage your invoice history</p>
            </div>
          </div>
        </Link>

        <Link
          to="/app/batch-invoices"
          className="bg-white p-6 rounded-lg shadow hover:shadow-md transition-shadow border-l-4 border-indigo-500 text-left w-full"
        >
          <div className="flex items-center">
            <TableCellsIcon className="h-8 w-8 text-indigo-500" />
            <div className="ml-4">
              <h3 className="text-lg font-medium text-gray-900">Bulk Invoices</h3>
              <p className="text-sm text-gray-600">Create or manage multiple invoices together</p>
            </div>
          </div>
        </Link>

        <button
          onClick={() => setShowBulkManager(true)}
          className="bg-white p-6 rounded-lg shadow hover:shadow-md transition-shadow border-l-4 border-purple-500 text-left w-full"
        >
          <div className="flex items-center">
            <CloudArrowUpIcon className="h-8 w-8 text-purple-500" />
            <div className="ml-4">
              <h3 className="text-lg font-medium text-gray-900">Bulk Submit Invoices</h3>
              <p className="text-sm text-gray-600">Submit multiple validated invoices at once</p>
            </div>
          </div>
        </button>

        <Link
          to="/app/reference-data"
          className="bg-white p-6 rounded-lg shadow hover:shadow-md transition-shadow border-l-4 border-orange-500"
        >
          <div className="flex items-center">
            <QrCodeIcon className="h-8 w-8 text-orange-500" />
            <div className="ml-4">
              <h3 className="text-lg font-medium text-gray-900">FBR Reference Data</h3>
              <p className="text-sm text-gray-600">Manage HS codes, UOM, and other reference data</p>
            </div>
          </div>
        </Link>

        <Link
          to="/app/reports"
          className="bg-white p-6 rounded-lg shadow hover:shadow-md transition-shadow border-l-4 border-emerald-500"
        >
          <div className="flex items-center">
            <ChartBarIcon className="h-8 w-8 text-emerald-500" />
            <div className="ml-4">
              <h3 className="text-lg font-medium text-gray-900">Reports & Analytics</h3>
              <p className="text-sm text-gray-600">Comprehensive reporting and business insights</p>
            </div>
          </div>
        </Link>
      </div>
      )}

      {/* Statistics */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center">
              <ChartBarIcon className="h-8 w-8 text-blue-500" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Invoices</p>
                <p className="text-2xl font-bold text-gray-900">{stats.total_invoices}</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center">
              <CheckCircleIcon className="h-8 w-8 text-green-500" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Submitted</p>
                <p className="text-2xl font-bold text-gray-900">{stats.submitted_invoices}</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center">
              <ClockIcon className="h-8 w-8 text-yellow-500" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Pending</p>
                <p className="text-2xl font-bold text-gray-900">{stats.pending_invoices}</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center">
              <ExclamationTriangleIcon className="h-8 w-8 text-red-500" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Errors</p>
                <p className="text-2xl font-bold text-gray-900">{stats.error_invoices}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Recent Invoices */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Recent Invoices</h3>
        </div>
        <div className="divide-y divide-gray-200">
          {recentInvoices.length > 0 ? (
            recentInvoices.map((invoice) => (
              <div key={invoice.id} className="px-6 py-4 hover:bg-gray-50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    {getStatusIcon(invoice.status)}
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {invoice.buyer_business_name || 'No buyer specified'}
                      </p>
                      <p className="text-sm text-gray-600">
                        {new Date(invoice.invoice_date).toLocaleDateString()} • 
                        {invoice.item_count} item{invoice.item_count !== 1 ? 's' : ''}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(invoice.status)}`}>
                      {invoice.status}
                    </span>
                    <p className="text-sm font-medium text-gray-900">
                      Rs. {invoice.total_amount ? parseFloat(invoice.total_amount).toLocaleString() : '0'}
                    </p>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="px-6 py-8 text-center">
              <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No invoices yet</h3>
              <p className="mt-1 text-sm text-gray-500">
                Get started by creating your first invoice.
              </p>
              <div className="mt-6">
                <Link
                  to="/app/scenarios"
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                >
                  <DocumentPlusIcon className="-ml-1 mr-2 h-5 w-5" />
                  Create Invoice
                </Link>
              </div>
            </div>
          )}
        </div>
        {recentInvoices.length > 0 && (
          <div className="px-6 py-3 border-t border-gray-200">
            <Link
              to="/app/invoices"
              className="text-sm font-medium text-blue-600 hover:text-blue-500"
            >
              View all invoices →
            </Link>
          </div>
        )}
      </div>

      {/* Bulk Invoice Manager Modal */}
      {showBulkManager && (
        <BulkInvoiceManager
          onClose={() => setShowBulkManager(false)}
        />
      )}
    </div>
  );
};

export default Dashboard;
